import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Auth } from '../interfaces/auth';
import { UserProfile } from '../interfaces/UserProfile';
import { UserService } from './user.service';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, mergeMap, of, tap, throwError } from 'rxjs';
// import { GeneralService } from './general.service';


@Injectable({
     providedIn: 'root'
})
export class AuthenticationService {
     apiUrl: string = environment.NODE_ENV;
     hasAgreedToTerms: boolean = false;
     deepLink: string = '';
     userProfile?: UserProfile;

     constructor(private cookieService: CookieService,
          private router: Router,private http: HttpClient,
          private userService: UserService) { }

     setUser(): boolean {
          const auth = this.cookieService.get('userJwt');
          this.cookieService.delete('userJwt');
           const paths = ['','/', '/app', '/app/'];
          const domains = [window.location.hostname, `.${window.location.hostname}`, ''];
               
          paths.forEach(path => {
               domains.forEach(domain => {
                    document.cookie = `userJwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
               });
          });

          if (auth) {
               // put auth token into browser store
               localStorage.setItem('user', auth);
               return true;
          }
          return false;
     }

     getRoles(): string[] {
          const user = this.getUser();
          if (!user) {
               return [];
          } else {
               const token = user.token;
               if (token) {
                    const parsed = this.parseJwt(token);
                    return parsed.roles as string[];
               }
          }
          return [];
     }

     getUser() {
          const userString: string = localStorage.getItem('user') as string;
          const user: Auth = JSON.parse(userString);
          return user;
     }

     getUserName(): string {
          let username = localStorage.getItem('username') as string;
          if (!username) {
               const user = this.getUser();
               if (!user) {
                    return '';
               }
               const token = user.token;
               if (token) {
                    const parsed = this.parseJwt(token);
                    username = parsed.sub;
                    localStorage.setItem('username', username);
               }
          }
          return username;
     }

     parseJwt(token: any) {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
               atob(base64)
                    .split('')
                    .map(function (c) {
                         return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    })
                    .join('')
          );
          return JSON.parse(jsonPayload);
     }

     refreshToken(): Observable<any> {
          const userString: string = localStorage.getItem('user') as string;
          const storedUser: Auth = JSON.parse(userString);
          //const storedUser: Auth = getters.user;
          if (!storedUser) {
               this.router.navigateByUrl('/signin');
               return of(true);
          }else{
               return this.userService.refresh(storedUser).pipe(
                    tap((auth: any) => {
                         if (auth) {
                              // store user details and jwt token in local storage to keep the user logged in between page refreshes
                              localStorage.setItem('user', JSON.stringify(auth));
                              return of(true); // Return a resolved observable
                         } else {
                              return throwError(false); // Return a rejected observable
                         }
                    }),
                    catchError((error: any) => {
                         console.error(error);
                         this.logout();
                         return throwError(error); // Return an error observable
                    })
               );
          }
     }

     logout() {
          const url = `${environment.APP_API_URL}/api/v1/ActionLogs/Log`;
          const body = {
               action: 'logout',
               authenticatedEmail: this.getUserName()
          };
          this.http.post(url, body).subscribe();
          localStorage.clear();

          const LOGOFF_URL = `${environment.APP_LOGIN_GOV_LOGOFF_URL}`;
          const CLIENT_ID = `${environment.APP_LOGIN_GOV_CLIENT_ID}`;
          const REDIRECT_URI = `${environment.APP_PSP_BASE_URL}/`;
          window.location.href = `${LOGOFF_URL}?client_id=${CLIENT_ID}&post_logout_redirect_uri=${REDIRECT_URI}`;
     }

     setHasAgreedToTerms(hasAgreed: boolean) {
          localStorage.setItem('hasAgreedToTerms', JSON.stringify(hasAgreed));
          this.hasAgreedToTerms = hasAgreed;
     }

     getHasAgreedToTerms(): boolean {
          const hasAgreedToTermsString: string = localStorage.getItem('hasAgreedToTerms') as string;
          const hasAgreedToTerms: boolean = JSON.parse(hasAgreedToTermsString);
          return hasAgreedToTerms;
     }

     setAccountType(accountType: string): void {
          localStorage.setItem('accountType', accountType);
     }

     getAccountType() {
          const accountType: string = localStorage.getItem('accountType') as string;
          return accountType;
     }

     setDeepLink(deepLink: string) {
          this.deepLink = deepLink;
          localStorage.setItem('deepLink', JSON.stringify(deepLink));
     }

     getDeepLink() {
          const deepLinkString: string = localStorage.getItem('deepLink') as string;
          const deepLink: string = JSON.parse(deepLinkString);
          return deepLink;
     }

     setDeepLinkResolved(deepLinkResolved: boolean) {
          localStorage.setItem('deepLinkResolved', JSON.stringify(deepLinkResolved));
     }

     isDeeplinkResolved(): boolean {
          const deepLinkResolvedString: string = localStorage.getItem('deepLinkResolved') as string;
          const deepLinkResolved: boolean = JSON.parse(deepLinkResolvedString);
          return deepLinkResolved;
     }

     setUserProfile(userProfile: UserProfile) {
          localStorage.setItem('userprofile', JSON.stringify(userProfile));
          this.userProfile = userProfile;
     }

     getUserProfile(): UserProfile | undefined {
          const profileString = localStorage.getItem('userprofile') as string;
          if (profileString) {
               this.userProfile = JSON.parse(profileString);
          }
          return this.userProfile;
     }

     setIsAuditAcknowledgeBypassed(isBypassed: boolean) {
          localStorage.setItem('iaab', JSON.stringify(isBypassed));
     }

     isAuditAcknowledgeBypassed(): boolean {
          const iaab: string = localStorage.getItem('iaab') as string;
          if (iaab) {
               const isByPassed: boolean = JSON.parse(iaab) || false;
               return isByPassed;
          } else {
               return false;
          }
     }

     setIsAuditAcknowledged(isAuditAcknowledged: boolean) {
          localStorage.setItem('isAuditAcknowledged', JSON.stringify(isAuditAcknowledged));
     }

     isAuditAcknowledged(): boolean {
          const isAuditAcknowledged: string = localStorage.getItem('isAuditAcknowledged') as string;
          if (isAuditAcknowledged) {
               const isByPassed: string = JSON.parse(isAuditAcknowledged);
               return isAuditAcknowledged == 'true';
          } else {
               return false;
          }
     }

}
